import {
  differenceInCalendarDays,
  endOfDay,
  format,
  formatDistanceStrict,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';

export const parseApiLocalDate = (dateStr: string | null | undefined): Date | null => {
  // Use for parsing API Date to js date object
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
};

export const parseFormDate = (dateStr: Date | null): string | null => {
  // Use to parse form date to an iso api date string
  if (!dateStr) return null;
  const year = dateStr.getFullYear();
  const month = String(dateStr.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const day = String(dateStr.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** A date-range filter's value: API local dates ("YYYY-MM-DD"), either end optional. */
export type DateRange = { from?: string; to?: string };

/**
 * The start / end of a local day ("YYYY-MM-DD") as an ISO instant — what a from / to
 * date filter sends to a DateTimeField range, so "Oct 4" means the viewer's Oct 4
 * whatever timezone the server runs in. Undefined for no / invalid date.
 */
export const localDayStartIso = (dateStr: string | null | undefined): string | undefined => {
  const date = parseApiLocalDate(dateStr);
  return date ? startOfDay(date).toISOString() : undefined;
};

export const localDayEndIso = (dateStr: string | null | undefined): string | undefined => {
  const date = parseApiLocalDate(dateStr);
  return date ? endOfDay(date).toISOString() : undefined;
};

export const formDateToApiDateTime = (dateStr: string | null | undefined): string | undefined => {
  // Converts a date input value (YYYY-MM-DD) to a naive datetime string for API DateTimeFields.
  // The backend localizes naive datetimes to the server timezone, keeping the chosen date stable.
  if (!dateStr) return undefined;
  return `${dateStr}T00:00:00`;
};

interface FormatApiDateOptions {
  dateFormat?: string;
  prefix?: string;
  emptyLabel?: string;
}

export const formatApiDate = (
  // Use to format API Date fields and avoid timezone shift issues, will display the date exactly as returned in API
  // Optional prefix label E.g. 'Starts on' and Optional empty label E.g. '-' or 'No Start Date'
  dateStr: string | null | undefined,
  { dateFormat = 'MMM d, yyyy', prefix, emptyLabel = '-' }: FormatApiDateOptions = {}
): string => {
  const date = parseApiLocalDate(dateStr);
  if (!date) return emptyLabel;
  const formatted = format(date, dateFormat);
  return prefix ? `${prefix} ${formatted}` : formatted;
};

export const formatDateTo24Hr = (
  // Formats a date string or Date object to 24-hour time format
  // Accepts an optional format string, defaults to 'MM/dd/yyyy HH:mm'
  dateInput: string | Date | null | undefined,
  dateFormat: string = 'MM/dd/yyyy HH:mm'
): string => {
  if (!dateInput) return '';
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return '';
  return format(date, dateFormat);
};

export interface DateTemporalityOptions {
  // Apply warning color when the date is within this many days from today (inclusive)
  warningWithinDays?: number; // default: 30
  // Anything below this (i.e., negative daysUntil) is considered critical. Default 0 (past dates)
  criticalUpperLimitDays?: number; // default: 0
  // Tailwind classes for each state (override if needed)
  warningClass?: string; // default: 'tw:text-amber-600'
  criticalClass?: string; // default: 'tw:text-red-600'
}

/**
 * Returns a Tailwind class name based on how close the given date is to today.
 * - Past dates (daysUntil < criticalUpperLimitDays) → criticalClass (red)
 * - Dates within warningWithinDays (daysUntil <= warningWithinDays) → warningClass (amber)
 * - Otherwise → undefined (no additional color)
 *
 * Accepts API local dates (YYYY-MM-DD), full ISO strings, or Date objects.
 */
export const getDateTemporalityClass = (
  dateInput: string | Date | null | undefined,
  options: DateTemporalityOptions = {}
): string | undefined => {
  const {
    warningWithinDays = 30,
    criticalUpperLimitDays = 0,
    warningClass = 'tw:text-stoplight-warning',
    criticalClass = 'tw:text-stoplight-danger',
  } = options;

  const temporality = getDateTemporality(dateInput, { warningWithinDays, criticalUpperLimitDays });
  if (temporality === 'critical') return criticalClass;
  if (temporality === 'warning') return warningClass;
  return undefined;
};

/**
 * How far a date is from today, in words: "in 2 days", "5 days ago", "today",
 * "in 3 months". Whole calendar days (the time of day never matters); larger spans
 * roll up to months / years. Undefined for no / invalid date.
 *
 * Accepts API local dates (YYYY-MM-DD), full ISO strings, or Date objects.
 */
export const formatRelativeDays = (
  dateInput: string | Date | null | undefined
): string | undefined => {
  if (!dateInput) return undefined;
  const date =
    typeof dateInput === 'string'
      ? (parseApiLocalDate(dateInput) ?? new Date(dateInput))
      : dateInput;
  if (!date || isNaN(date.getTime())) return undefined;

  const today = startOfDay(new Date());
  if (differenceInCalendarDays(date, today) === 0) return 'today';
  return formatDistanceStrict(startOfDay(date), today, { addSuffix: true });
};

export type DateTemporality = 'critical' | 'warning';

/**
 * How close the given date is to today — the rule behind `getDateTemporalityClass`,
 * for callers that show something other than a text colour (e.g. an "Expired" /
 * "Expiring soon" badge):
 * - 'critical': the date has passed (daysUntil < criticalUpperLimitDays)
 * - 'warning': within warningWithinDays (inclusive, default 30)
 * - undefined: further out, or no / invalid date
 *
 * Accepts API local dates (YYYY-MM-DD), full ISO strings, or Date objects.
 */
export const getDateTemporality = (
  dateInput: string | Date | null | undefined,
  options: Pick<DateTemporalityOptions, 'warningWithinDays' | 'criticalUpperLimitDays'> = {}
): DateTemporality | undefined => {
  const { warningWithinDays = 30, criticalUpperLimitDays = 0 } = options;

  if (!dateInput) return undefined;

  // Try to parse API local date first to avoid timezone shifts; fallback to Date parsing
  let date: Date | null = null;
  if (typeof dateInput === 'string') {
    date = parseApiLocalDate(dateInput) ?? new Date(dateInput);
  } else {
    date = dateInput;
  }

  if (!date || isNaN(date.getTime())) return undefined;

  const daysUntil = differenceInCalendarDays(date, new Date());

  if (daysUntil < criticalUpperLimitDays) return 'critical';
  if (daysUntil <= warningWithinDays) return 'warning';
  return undefined;
};

/**
 * Formats a duration string (HH:MM:SS) into a human-readable format like "12h" or "8h 30m".
 */
export const formatDuration = (duration: string | null | undefined): string => {
  if (!duration) return '--';
  // DRF DurationField serializes as "D HH:MM:SS" for days or "HH:MM:SS" for hours only
  const match = duration.match(/(?:(\d+)\s+)?(\d+):(\d+):(\d+)/);
  if (match) {
    const days = parseInt(match[1] || '0', 10);
    const hours = days * 24 + parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    if (minutes > 0) return `${hours}h ${minutes}m`;
    return `${hours}h`;
  }
  return duration;
};

/** Default date pattern for the year-aware formatters: day-of-week, month, day. */
export const DEFAULT_DATE_PATTERN = 'EEE MMM d';

/**
 * The year-aware core: formats `date` with `pattern`, appending ", yyyy" only when the
 * date's year differs from the current year (or from `otherYear`, for ranges whose
 * end year should also show when it differs from the start year).
 */
const formatWithYearIfNeeded = (
  date: Date,
  pattern: string = DEFAULT_DATE_PATTERN,
  otherYear?: number
): string => {
  const year = date.getFullYear();
  const showYear =
    year !== new Date().getFullYear() || (otherYear !== undefined && year !== otherYear);
  return format(date, showYear ? `${pattern}, yyyy` : pattern);
};

/**
 * Formats a single date string, showing year only when it differs from the current year.
 * Default pattern includes day-of-week: "Mon Apr 11" or "Mon Apr 11, 2025".
 * Pass a pattern to change the base (e.g. 'MMM d' → "Apr 11" / "Apr 11, 2025").
 */
const formatSingleDate = (dateStr: string, pattern: string = DEFAULT_DATE_PATTERN): string => {
  const date = parseISO(dateStr);
  // Fall back to the raw string rather than throwing: callers render
  // user-supplied data (e.g. ingest file rows), and date-fns format()
  // throws a RangeError on an invalid Date.
  if (!isValid(date)) return dateStr;
  return formatWithYearIfNeeded(date, pattern);
};

/**
 * Formats a time-only string (HH:mm:ss or HH:mm) safely, falling back to the
 * raw string when it isn't a parseable time (format() throws on Invalid Date).
 */
const formatTimePart = (timeStr: string, timeFormat: string): string => {
  const parsed = parseISO(`2000-01-01T${timeStr}`);
  return isValid(parsed) ? format(parsed, timeFormat) : timeStr;
};

const formatDateParts = (
  start_on: string,
  end_on: string
): { startDateStr: string; endDateStr: string | null } => {
  const startDate = parseISO(start_on);
  const endDate = parseISO(end_on);

  const startDateStr = formatWithYearIfNeeded(startDate);
  // end date also shows its year when it differs from the start's year
  const endDateStr =
    start_on !== end_on
      ? formatWithYearIfNeeded(endDate, DEFAULT_DATE_PATTERN, startDate.getFullYear())
      : null;

  return { startDateStr, endDateStr };
};

/**
 * Formats a shift's date/time range for display.
 * Shows year only when it differs from the current year.
 * Shows end date only when it differs from start date (overnight/multi-day shifts).
 *
 * Examples:
 *   Same day, current year: "Mon Apr 11 07:00 – 19:00"
 *   Overnight, current year: "Mon Apr 11 19:00 – Tue Apr 12 07:00"
 *   Different year: "Mon Apr 11, 2025 19:00 – Tue Apr 12, 2025 07:00"
 *   End date in different year from start: "Tue Dec 31 19:00 – Wed Jan 01, 2026 07:00"
 *
 * Accepts an object with start_on, start_time, end_on, end_time fields.
 */
export const formatDateTimeRange = (
  shift: {
    start_on?: string | null;
    start_time?: string | null;
    end_on?: string | null;
    end_time?: string | null;
  },
  timeFormat: string = 'HH:mm'
): string => {
  const { start_on, start_time, end_on, end_time } = shift;
  if (!start_on || !start_time || !end_on || !end_time) return '--';

  const { startDateStr, endDateStr } = formatDateParts(start_on, end_on);
  const startTimeStr = formatTimePart(start_time, timeFormat);
  const endTimeStr = formatTimePart(end_time, timeFormat);

  if (endDateStr) {
    return `${startDateStr} ${startTimeStr} – ${endDateStr} ${endTimeStr}`;
  }
  return `${startDateStr} ${startTimeStr} – ${endTimeStr}`;
};

export const formatDateRange = (date_range: {
  start_on?: string | null;
  end_on?: string | null;
}): string => {
  const { start_on, end_on } = date_range;
  if (!start_on || !end_on) return '--';

  const { startDateStr, endDateStr } = formatDateParts(start_on, end_on);

  if (endDateStr) {
    return `${startDateStr} – ${endDateStr}`;
  }
  return startDateStr;
};

/**
 * Formats a single date string for display, showing year only when it differs from the current year.
 * Default: "Mon Apr 11" or "Mon Apr 11, 2025". Override `pattern` for a different base,
 * e.g. formatDate(d, 'MMM d') → "Apr 11" / "Apr 11, 2025"; the year logic stays the same.
 */
export const formatDate = (
  dateStr: string | null | undefined,
  pattern: string = DEFAULT_DATE_PATTERN
): string => {
  if (!dateStr) return '--';
  return formatSingleDate(dateStr, pattern);
};

/**
 * Formats a date + time for display, showing year only when it differs from the current year.
 * E.g. "Mon Apr 11 07:00" or "Mon Apr 11, 2025 07:00"
 */
export const formatDateTime = (
  dateStr: string | null | undefined,
  timeStr: string | null | undefined,
  timeFormat: string = 'HH:mm'
): string => {
  if (!dateStr || !timeStr) return '--';
  const datePart = formatSingleDate(dateStr);
  const timePart = formatTimePart(timeStr, timeFormat);
  return `${datePart} ${timePart}`;
};

/**
 * Formats a full ISO timestamp (e.g. an API created_at) for display, showing
 * year only when it differs from the current year.
 * E.g. "Mon Apr 11 07:00" or "Mon Apr 11, 2025 07:00"
 */
export const formatTimestamp = (timestampStr: string | null | undefined): string => {
  if (!timestampStr) return '--';
  const date = parseISO(timestampStr);
  if (!isValid(date)) return timestampStr;
  return `${formatSingleDate(timestampStr)} ${format(date, 'HH:mm')}`;
};

/**
 * Formats a full ISO timestamp with the year always shown — the complete date and time
 * behind a short year-aware date (`formatDate(d, 'MMM d')`), e.g. in its tooltip.
 * E.g. "Oct 4, 2026 15:32"
 */
export const formatFullTimestamp = (timestampStr: string | null | undefined): string => {
  if (!timestampStr) return '--';
  const date = parseISO(timestampStr);
  if (!isValid(date)) return timestampStr;
  return format(date, 'MMM d, yyyy HH:mm');
};

/**
 * Formats a time-only string (HH:mm:ss or HH:mm) for display.
 * E.g. "07:00:00" → "07:00"
 */
export const formatTime = (
  timeStr: string | null | undefined,
  timeFormat: string = 'HH:mm'
): string => {
  if (!timeStr) return '--';
  return formatTimePart(timeStr, timeFormat);
};

/**
 * The same calendar day at a fixed local hour (minutes/seconds zeroed). Used to turn a
 * date-only choice into a send time, e.g. "send the request the morning of the 3rd".
 */
export const dateAtLocalHour = (date: Date, hour: number): Date => {
  const result = new Date(date);
  result.setHours(hour, 0, 0, 0);
  return result;
};

/** Is the given date today's calendar day (local time)? */
export const isLocalToday = (date: Date): boolean => {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};
